// Brochure content — verbatim from client. No additions.

export const HEADER = {
    title: "Strategic Reassessment & Operational Transformation",
    subtitle: "Aviation Cargo Advisory & Corporate Recovery Services",
};

export const FIRM_BACKGROUND = {
    heading: "Firm Background",
    paragraphs: [
        "The advisory approach is built upon multidisciplinary experience in aviation operations, corporate restructuring, regulatory compliance and crisis management environments.",
        "Over the last decade, the practice has focused on cargo operations in complex and unstable regions, supply chain disruption management, operational reassessment of distressed companies, route restructuring, rerouting strategies and executive advisory services for both independent and state-owned operators.",
        "The operational background includes direct management and dispatch coordination during high-risk and crisis scenarios, including aviation continuity support during the COVID-19 emergency period for Middle Eastern carriers.",
        "The methodology adopted reflects international aviation consulting standards commonly applied by leading industry advisory and auditing firms, combining operational diagnostics, financial assessment, governance review and implementation-oriented restructuring frameworks.",
        "The engagement is led by Alessandro Cisselo.",
        "Alessandro Cisselo holds a Law Degree with specialization in International Affairs and Constitutional Law, together with an MBA and advanced specialization in Company Restoration and Corporate Recovery.",
    ],
    certificationsHeading:
        "Professional certifications and technical qualifications include:",
    certifications: [
        "IATA TACT Air Cargo Solutions",
        "IATA Cargo Diploma Level 1 & 2",
        "IATA Air Cargo and Supply Chain Security",
        "IATA Safety Operations and Handling Security",
        "Deloitte & Partners — Audit, Accounting Guides & Risk Assessment",
        "Airsight — Aviation Auditing",
        "Lufthansa Group — TCO Third Country Operations",
        "Handling",
        "Aircraft Maintenance",
    ],
};

export const EXECUTIVE_CONTEXT = {
    heading: "Executive Context",
    paragraphs: [
        "The proposed intervention is structured as an integrated reassessment and transformation program aimed at restoring operational efficiency, commercial competitiveness, financial resilience and long-term strategic positioning.",
        "The assignment requires direct on-site execution, with access to operational departments, management teams, technical records and financial reporting, in order to ensure an accurate and actionable assessment.",
        "The approach combines aviation operational auditing, strategic advisory and organizational restructuring methodologies typically adopted in complex airline transformation programs.",
    ],
};

export const CORE_AREAS_HEADING = "Core Areas of Assessment & Repositioning";

export const CORE_AREAS = [
    {
        n: "1.",
        title: "Strategic Positioning & Business Model Review",
        intro:
            "Comprehensive reassessment of the company's market positioning, network logic and long-term commercial sustainability.",
        focus: [
            "Current business model effectiveness",
            "Cargo market positioning versus regional competitors",
            "Route portfolio profitability and strategic coherence",
            "Customer segmentation and contract structure",
            "Partnership and interline opportunities",
            "Fleet-to-network alignment",
            "Yield quality and revenue concentration",
            "Exposure to market cyclicality and geopolitical risk",
        ],
        deliverables: [
            "Strategic positioning assessment",
            "Market competitiveness review",
            "Commercial sustainability analysis",
            "Executive repositioning recommendations",
        ],
    },
    {
        n: "2.",
        title: "Operational Efficiency & Flight Operations Audit",
        intro:
            "Independent review of operational performance, process efficiency and execution standards across flight and ground operations.",
        focus: [
            "OCC and operational control structure",
            "Dispatch and operational planning efficiency",
            "Aircraft utilization performance",
            "OTP (On-Time Performance) drivers",
            "Crew productivity and rostering efficiency",
            "Fuel management practices",
            "Irregular operations handling",
            "Ground handling coordination",
            "Turnaround performance analysis",
            "Safety culture and operational governance",
        ],
        deliverables: [
            "Operational efficiency assessment",
            "Performance gap analysis",
            "Operational restructuring roadmap",
            "KPI framework and corrective actions",
        ],
    },
    {
        n: "3.",
        title: "Maintenance, Engineering & Technical Governance Review",
        intro:
            "Assessment of technical organization effectiveness, reliability management and maintenance governance.",
        focus: [
            "Maintenance program effectiveness",
            "Reliability trends and recurring defects",
            "Technical dispatch reliability",
            "Vendor and MRO management",
            "Inventory and spare parts optimization",
            "Maintenance cost structure",
            "Engineering planning processes",
            "Technical records and compliance controls",
            "Long-term fleet sustainability",
        ],
        deliverables: [
            "Engineering and maintenance assessment",
            "Reliability and cost optimization recommendations",
            "Technical governance review",
            "Fleet support efficiency plan",
        ],
    },
    {
        n: "4.",
        title: "Financial & Cost Structure Reassessment",
        intro:
            "High-level financial diagnostic focused on identifying structural inefficiencies and margin improvement opportunities.",
        focus: [
            "Cost base decomposition",
            "Fixed versus variable cost exposure",
            "Lease and financing obligations",
            "Fuel and operational cost drivers",
            "Vendor contract review",
            "Cash flow resilience",
            "Profitability by aircraft / route / customer segment",
            "Capital allocation priorities",
            "Financial reporting quality and management visibility",
        ],
        deliverables: [
            "Financial diagnostic summary",
            "Cost optimization opportunities",
            "Margin recovery framework",
            "Executive financial recommendations",
        ],
    },
    {
        n: "5.",
        title: "Commercial & Revenue Optimization",
        intro:
            "Review of revenue generation capability, commercial structure and cargo sales effectiveness.",
        focus: [
            "Revenue management practices",
            "Pricing governance",
            "Cargo load factor quality",
            "Forwarder and key account dependency",
            "Commercial organization structure",
            "Digital sales capability",
            "Capacity allocation logic",
            "Market diversification opportunities",
        ],
        deliverables: [
            "Commercial effectiveness review",
            "Revenue optimization framework",
            "Commercial restructuring recommendations",
            "Sales governance enhancement plan",
        ],
    },
    {
        n: "6.",
        title: "Organizational Structure & Leadership Alignment",
        intro:
            "Assessment of organizational effectiveness, governance clarity and decision-making architecture.",
        focus: [
            "Organizational design and reporting lines",
            "Management accountability structure",
            "Cross-functional coordination effectiveness",
            "Decision-making processes",
            "Talent capability assessment",
            "Succession and leadership resilience",
            "Internal communication and execution culture",
        ],
        deliverables: [
            "Organizational assessment",
            "Governance enhancement recommendations",
            "Leadership alignment observations",
            "Organizational restructuring priorities",
        ],
    },
    {
        n: "7.",
        title: "Compliance, Safety & Regulatory Readiness",
        intro:
            "Independent review of regulatory exposure and operational compliance robustness.",
        focus: [
            "IOSA / regulatory alignment",
            "Safety management system effectiveness",
            "Documentation and procedural compliance",
            "Audit preparedness",
            "Risk escalation mechanisms",
            "Regulatory reporting practices",
            "Operational compliance culture",
        ],
        deliverables: [
            "Compliance readiness assessment",
            "Safety governance observations",
            "Regulatory exposure overview",
            "Corrective action priorities",
        ],
    },
];

export const METHODOLOGY = {
    heading: "Methodology",
    intro: "The intervention will be conducted through:",
    items: [
        "Executive management interviews",
        "Operational and technical workshops",
        "Documentation and reporting review",
        "Data-driven performance analysis",
        "On-site operational observation",
        "Benchmarking against international cargo carrier standards",
        "Independent executive assessment",
    ],
};

export const EXPECTED_OUTCOME = {
    heading: "Expected Outcome",
    intro: "The engagement is designed to provide Management and Shareholders with:",
    items: [
        "A clear independent assessment of the company's current condition",
        "Identification of operational and financial inefficiencies",
        "Strategic repositioning opportunities",
        "A prioritized restructuring roadmap",
        "Executive-level recommendations for sustainable growth and operational resilience",
    ],
};

export const ENGAGEMENT_STRUCTURE = {
    heading: "Engagement Structure",
    subheading: "Proposed Initial Phase",
    items: [
        "On-site assessment in Taipei",
        "Executive interviews and operational review",
        "Preliminary diagnostic report",
        "Strategic findings presentation",
    ],
};

export const PROFESSIONAL_ENGAGEMENT = {
    heading: "Professional Engagement",
    text:
        "Professional terms, operational scope and execution phases are defined according to the complexity of the assignment, fleet structure, operational exposure and implementation requirements",
};

export const CLOSING_NOTE = {
    heading: "Closing Note",
    text:
        "The objective of the engagement is not limited to auditing existing structures, but to provide Management with a pragmatic and executable transformation framework capable of improving performance, governance, competitiveness and long-term enterprise value.",
};

export const CONTACT = {
    company: "Nador Sage International S.r.l.",
    emails: ["info@nadorsage.com"],
};
